import React, { useState } from "react";
import "../importStrukture/importStrukture.css";
import Button from "../../elements/button";
import Loading from "../../loading";
import { statusClass } from "../importStrukture";
import {
  validateImportFileLocally,
  postImportFile,
  importErrorMessage,
  downloadImportReport,
} from "../../../utils/importApi";

const STATUS_FILTERS = ["Sve", "NOVO", "VEĆ POSTOJI", "UPOZORENJE", "KONFLIKT", "GREŠKA", "INFO"];

/**
 * Popup za uvoz poslovnih procesa, mapa, procedura, koraka i rizika iz Excela
 * (samo Administrator). Isti tok kao uvoz strukture: Proveri, pa Uvezi.
 */
export default function ImportProcesa({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState("Sve");
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);

  const handleFile = (e) => {
    setError("");
    setPreview(null);
    setCreated(null);
    setFile(e.target.files?.[0] || null);
  };

  const handlePreview = async () => {
    const localError = validateImportFileLocally(file);
    if (localError) {
      setError(localError);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await postImportFile("/import/processes/preview", file);
      setPreview(data);
      setFilter("Sve");
      setAcknowledged(false);
    } catch (err) {
      setPreview(null);
      setError(importErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await postImportFile("/import/processes", file);
      setCreated(data.created);
      onImported?.();
    } catch (err) {
      setError(importErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleReport = async () => {
    try {
      await downloadImportReport({ rows: preview.rows, summary: {} });
    } catch (err) {
      setError(importErrorMessage(err));
    }
  };

  if (created) {
    return (
      <div className="loading">
        <div className="import-popup">
          <p className="import-popup-title">Uvoz je završen</p>
          <p className="import-popup-done">
            Uvezeno je {created.processes} poslovnih procesa, {created.maps} mapa
            procesa, {created.procedures} procedura sa {created.steps} koraka i{" "}
            {created.risks} formulara rizika.
            <br />
            Podaci na strani su osveženi.
          </p>
          <div className="import-popup-actions">
            <Button title="Zatvori" onClick={onClose} style={{ width: "140px" }} />
          </div>
        </div>
      </div>
    );
  }

  const rows =
    preview &&
    (filter === "Sve" ? preview.rows : preview.rows.filter((r) => r.status === filter));
  const hasWarnings = preview && preview.rows.some((r) => r.status === "UPOZORENJE");
  const canImport = preview && preview.canCommit && (!hasWarnings || acknowledged);
  const s = preview?.summary;

  return (
    <div className="loading">
      <div className="import-popup">
        <p className="import-popup-title">Uvoz procesa, mapa i rizika iz Excela</p>

        <div className="import-popup-file-row">
          <input className="import-popup-file-input" type="file" accept=".xlsx" onChange={handleFile} />
          {file && <span className="import-popup-file-name">{file.name}</span>}
        </div>
        <p className="import-popup-note">
          Šablon i popunjen primer se preuzimaju dugmadima na strani Liste
          poslovnih procesa. Preduslov je da organizaciona struktura, radna mesta i
          zaposleni već postoje. „Proveri" pokaže šta će se uvesti, „Uvezi" upisuje.
          Postojeći procesi, mape i rizici se ne menjaju; proces koji već ima
          aktivnu mapu zadržava postojeću.
        </p>
        {error && <p className="import-popup-error">{error}</p>}

        {preview && (
          <>
            <div className="import-popup-summary">
              <span>Procesi: {s.processes.new} novo, {s.processes.existing} već postoji</span>
              <span>Mape: {s.maps.new} novo, {s.maps.conflict} već postoji</span>
              <span>Procedure: {s.procedures.new} novo ({s.steps.new} koraka)</span>
              <span>Rizici: {s.risks.new} novo, {s.risks.existing} već postoji</span>
            </div>

            <div className="import-popup-filters">
              {STATUS_FILTERS.map((st) => (
                <button
                  key={st}
                  type="button"
                  className={filter === st ? "import-popup-filter active" : "import-popup-filter"}
                  onClick={() => setFilter(st)}
                >
                  {st === "Sve"
                    ? `Sve (${preview.rows.length})`
                    : `${st} (${preview.rows.filter((r) => r.status === st).length})`}
                </button>
              ))}
            </div>

            <div className="import-popup-table-wrapper">
              <table className="import-popup-table">
                <thead>
                  <tr>
                    <th style={{ width: "150px" }}>Sheet</th>
                    <th style={{ width: "45px" }}>Red</th>
                    <th style={{ width: "110px" }}>Entitet</th>
                    <th style={{ width: "180px" }}>Naziv</th>
                    <th style={{ width: "110px" }}>Status</th>
                    <th>Poruka</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.sheet}-${r.row}-${i}`}>
                      <td>{r.sheet}</td>
                      <td>{r.row}</td>
                      <td>{r.entity}</td>
                      <td>{r.name}</td>
                      <td>
                        <span className={`import-popup-status ${statusClass(r.status)}`}>{r.status}</span>
                      </td>
                      <td>{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasWarnings && (
              <label className="import-popup-checkbox">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                Pregledao sam upozorenja i potvrđujem uvoz.
              </label>
            )}
            {!preview.canCommit && (
              <p className="import-popup-error">
                Dok postoje redovi sa statusom GREŠKA uvoz nije moguć; ispravite fajl i
                proverite ga ponovo.
              </p>
            )}
          </>
        )}

        <div className="import-popup-actions">
          <Button title="Proveri" onClick={handlePreview} disabled={!file || loading} style={{ width: "140px" }} />
          <Button title="Uvezi" onClick={handleImport} disabled={!canImport || loading} style={{ width: "140px" }} />
          {preview && (
            <Button title="Izveštaj provere (XLSX)" onClick={handleReport} disabled={loading} style={{ width: "190px" }} />
          )}
          <Button title="Otkaži" onClick={onClose} style={{ width: "140px" }} />
        </div>
      </div>
      <Loading isLoading={loading} />
    </div>
  );
}
